import React from "react";

import * as Styled from "./styles";
import { CircularProgress } from "@mui/material";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { GaugeChart } from "components/GaugeChart";
import { InfoModalButton } from "components/InfoModalButton";
import dayjs, { Dayjs } from "dayjs";
import { FaStar } from "react-icons/fa";
import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  Legend,
  ResponsiveContainer,
  BarChart,
  XAxis,
  YAxis,
  Bar,
  LabelList,
  CartesianGrid,
  PieLabelRenderProps,
} from "recharts";
import { useAuth } from "shared/providers/auth";
import { IDashboardData } from "shared/types/Indicators";
import { getDashboardData } from "requests/Indicators";

const PIE_COLORS = [
  "#0b5fff",
  "#20c997",
  "#ffb400",
  "#7c4dff",
  "#16a2b8",
  "#ef476f",
];

const formatPercentValue = (value?: number) =>
  Math.max(0, Math.round(value ?? 0));

const renderPieLabel = ({
  cx = 0,
  cy = 0,
  midAngle = 0,
  innerRadius = 0,
  outerRadius = 0,
  percent = 0,
}: PieLabelRenderProps) => {
  const RADIAN = Math.PI / 180;
  const safeInner = Number(innerRadius) || 0;
  const safeOuter = Number(outerRadius) || 0;
  const safeCx = Number(cx) || 0;
  const safeCy = Number(cy) || 0;
  const safeAngle = Number(midAngle) || 0;
  const radius = safeInner + (safeOuter - safeInner) * 0.55;
  const x = safeCx + radius * Math.cos(-safeAngle * RADIAN);
  const y = safeCy + radius * Math.sin(-safeAngle * RADIAN);

  return (
    <text
      x={x}
      y={y}
      fill="#0b1d5d"
      textAnchor={x > cx ? "start" : "end"}
      dominantBaseline="central"
      fontSize={12}
      fontWeight={700}
    >
      {`${Math.round(percent * 100)}%`}
    </text>
  );
};

const Indicators: React.FC = () => {
  const [loadingCurrent, setLoadingCurrent] = React.useState<boolean>(true);
  const { user } = useAuth();

  const [selectedDate, setSelectedDate] = React.useState<Dayjs>(dayjs());
  const [presetMonth, setPresetMonth] = React.useState<
    "current" | "previous" | null
  >("current");

  const [dashboardData, setDashboardData] = React.useState<IDashboardData>(
    {} as IDashboardData
  );

  const getDashboard = React.useCallback(async () => {
    try {
      setLoadingCurrent(true);
      const lastDayOfMonth = selectedDate.endOf("month").format("YYYY-MM-DD");
      const ano = selectedDate.format("YYYY");
      const mes = selectedDate.format("M");

      const { status, data } = await getDashboardData({
        userId: user.data.id,
        date: lastDayOfMonth,
        ano,
        mes,
      });

      if ([200, 201].includes(status)) {
        setDashboardData(data.data);
      }
    } catch (error) {
      console.error("Erro ao carregar dashboard do dentista", error);
    } finally {
      setLoadingCurrent(false);
    }
  }, [selectedDate, user.data.id]);

  React.useEffect(() => {
    getDashboard();
  }, [getDashboard]);

  const handlePresetMonth = (offset: number, preset: "current" | "previous") => {
    setSelectedDate(dayjs().add(offset, "month"));
    setPresetMonth(preset);
  };

  const monthLabel = selectedDate.format("MMMM/YYYY");
  const updatedAtLabel = selectedDate.endOf("month").format("DD/MM/YYYY");
  const blocos = React.useMemo(() => dashboardData?.blocos ?? [], [dashboardData?.blocos]);
  const graficos = dashboardData?.graficos;

  const ratingValue = React.useMemo(() => {
    const notaBlock = blocos.find((block) =>
      block.titulo?.toLowerCase?.().includes("nota")
    );
    return notaBlock ? formatPercentValue(notaBlock.valor) : 0;
  }, [blocos]);

  const starIntensity = React.useMemo(() => {
    const normalized = Math.min(Math.max(ratingValue, 0), 10) / 10;
    return {
      opacity: 0.6 + normalized * 0.4,
      filter: `drop-shadow(0 0 ${6 + normalized * 10}px rgba(255, 204, 0, ${
        0.45 + normalized * 0.55
      }))`,
    };
  }, [ratingValue]);

  const gaugeCards = React.useMemo(
    () => [
      {
        title: "Índice de Atendimento",
        value: formatPercentValue(graficos?.indiceAtendimento?.valor),
        description: graficos?.indiceAtendimento?.descricao,
        tone: "positive" as const,
      },
      {
        title: "Índice de Recorrência",
        value: formatPercentValue(graficos?.indiceRecorrencia?.valor),
        description: graficos?.indiceRecorrencia?.descricao,
        tone: "neutral" as const,
      },
      {
        title: "Índice de Evasão",
        value: formatPercentValue(graficos?.indiceEvasao?.valor),
        description: graficos?.indiceEvasao?.descricao,
        tone: "alert" as const,
        isEvasion: true,
      },
    ],
    [graficos]
  );

  const renderValue = (valor?: number, type?: string) => {
    if (type === "Pacientes") {
      return (valor ?? 0).toLocaleString("pt-BR");
    }

    return `${formatPercentValue(valor)}%`;
  };

  return (
    <Styled.Page>
      {loadingCurrent ? (
        <Styled.Loader>
          <CircularProgress size={48} />
        </Styled.Loader>
      ) : (
        <>
          <Styled.HeaderRow>
            <div>
              <Styled.PageTitle>Meus indicadores</Styled.PageTitle>
              <Styled.Subtitle>
                Visão mensal com leitura rápida e pronta para toque no tablet.
              </Styled.Subtitle>
            </div>

            <Styled.ActionsRow>
              <Styled.QuickButton
                $active={presetMonth === "current"}
                onClick={() => handlePresetMonth(0, "current")}
              >
                Mês atual
              </Styled.QuickButton>
              <Styled.QuickButton
                $active={presetMonth === "previous"}
                onClick={() => handlePresetMonth(-1, "previous")}
              >
                Último mês
              </Styled.QuickButton>

              <LocalizationProvider
                dateAdapter={AdapterDayjs}
                adapterLocale="pt-br"
              >
                <DatePicker
                  views={["year", "month"]}
                  value={selectedDate}
                  onChange={(newValue) => {
                    if (newValue) {
                      setSelectedDate(newValue);
                      setPresetMonth(null);
                    }
                  }}
                  format="MMMM/YYYY"
                  minDate={dayjs("2010-01-01")}
                  maxDate={dayjs().endOf("month")}
                  slotProps={{
                    textField: {
                      size: "small",
                      variant: "outlined",
                      placeholder: "Selecione o mês",
                    },
                    openPickerButton: {
                      sx: {
                        color: "#0b5fff",
                      },
                    },
                  }}
                />
              </LocalizationProvider>
            </Styled.ActionsRow>
          </Styled.HeaderRow>

          <Styled.IdentityCard>
            <div>
              <Styled.IdentityLabel>Dentista</Styled.IdentityLabel>
              <Styled.IdentityName>{user.data.dentist.name}</Styled.IdentityName>
              <Styled.IdentityDate>
                Atualizado até {updatedAtLabel}
              </Styled.IdentityDate>
            </div>

            <Styled.RatingPill>
              <FaStar color="#ffcc00" size={18} style={starIntensity} />
              <span>{ratingValue || "--"}</span>
              <InfoModalButton
                message="Avaliação consolidada do dentista."
                isAbsolute={false}
              />
            </Styled.RatingPill>
          </Styled.IdentityCard>

          {dashboardData && (
            <>
              <Styled.Section>
                <Styled.SectionTitle>Resumo rápido</Styled.SectionTitle>
                <Styled.MetricsGrid>
                  {blocos.map((item, index) => (
                    <Styled.MetricCard key={`${item.titulo}-${index}`}>
                      <Styled.MetricHeader>
                        <Styled.MetricTitle>{item.titulo}</Styled.MetricTitle>
                        <Styled.MetricBadge $type={item.tipo}>
                          {item.tipo === "Pacientes" ? "Pacientes" : "%"}
                        </Styled.MetricBadge>
                      </Styled.MetricHeader>
                      <Styled.MetricValue>
                        {renderValue(item.valor, item.tipo)}
                      </Styled.MetricValue>
                      <Styled.MetricFooter>
                        <span>{monthLabel}</span>
                        <InfoModalButton
                          message={item.descricao ?? "Sem descrição disponível."}
                          isAbsolute={false}
                        />
                      </Styled.MetricFooter>
                    </Styled.MetricCard>
                  ))}
                </Styled.MetricsGrid>
              </Styled.Section>

              <Styled.Section>
                <Styled.SectionTitle>Índices críticos</Styled.SectionTitle>
                <Styled.GaugeGrid>
                  {gaugeCards.map((gauge) => (
                    <Styled.GaugeCard key={gauge.title}>
                      <Styled.GaugeHeader>
                        <div>
                          <Styled.MetricTitle>{gauge.title}</Styled.MetricTitle>
                        </div>
                      </Styled.GaugeHeader>

                      <GaugeChart
                        value={gauge.value}
                        isEvasion={gauge.isEvasion}
                      />
                      <Styled.GaugeValue>{gauge.value}%</Styled.GaugeValue>
                      <Styled.GaugeFooter>
                        <InfoModalButton
                          message={gauge.description ?? "Sem descrição disponível."}
                          isAbsolute={false}
                        />
                      </Styled.GaugeFooter>
                    </Styled.GaugeCard>
                  ))}
                </Styled.GaugeGrid>
              </Styled.Section>

              <Styled.Section>
                <Styled.SectionTitle>Composição da carteira</Styled.SectionTitle>
                <Styled.ChartGrid>
                  <Styled.ChartCard>
                    <Styled.ChartCardHeader>
                      <div>
                        <Styled.ChartTitle>Pacientes em Carteira</Styled.ChartTitle>
                        <Styled.ChartDescription>
                          Distribuição por status, pronto para toque.
                        </Styled.ChartDescription>
                      </div>
                      <InfoModalButton
                        message={
                          graficos?.pizzaCarteira?.descricao ??
                          "Distribuição da carteira de pacientes."
                        }
                        isAbsolute={false}
                      />
                    </Styled.ChartCardHeader>
                    <Styled.ChartBody>
                      <ResponsiveContainer width="100%" height={380}>
                        <PieChart>
                          <defs>
                            <linearGradient id="pieCarteiraShadow" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="0%" stopColor="#0b5fff" stopOpacity={0.12} />
                              <stop offset="100%" stopColor="#0b5fff" stopOpacity={0} />
                            </linearGradient>
                          </defs>
                          <Pie
                            data={graficos?.pizzaCarteira?.dados ?? []}
                            dataKey="valor"
                            nameKey="label"
                            cx="50%"
                            cy="45%"
                            innerRadius={55}
                            outerRadius={95}
                            labelLine={false}
                            label={renderPieLabel}
                          >
                            {graficos?.pizzaCarteira?.dados?.map((_, index) => (
                              <Cell
                                key={`carteira-${index}`}
                                fill={PIE_COLORS[index % PIE_COLORS.length]}
                                stroke="none"
                              />
                            ))}
                          </Pie>
                          <Tooltip
                            formatter={(value: number, name: string) => [
                              value,
                              name,
                            ]}
                          />
                          <Legend 
                            verticalAlign="bottom" 
                            height={80}
                            wrapperStyle={{ paddingTop: "20px" }}
                          />
                        </PieChart>
                      </ResponsiveContainer>
                    </Styled.ChartBody>
                  </Styled.ChartCard>

                  <Styled.ChartCard>
                    <Styled.ChartCardHeader>
                      <div>
                        <Styled.ChartTitle>Pacientes Atendidos</Styled.ChartTitle>
                        <Styled.ChartDescription>
                          Pizzas interativas com legenda alinhada para tablets.
                        </Styled.ChartDescription>
                      </div>
                      <InfoModalButton
                        message={
                          graficos?.pizzaAtendidos?.descricao ??
                          "Pacientes atendidos no mês."
                        }
                        isAbsolute={false}
                      />
                    </Styled.ChartCardHeader>
                    <Styled.ChartBody>
                      <ResponsiveContainer width="100%" height={380}>
                        <PieChart>
                          <defs>
                            <linearGradient id="pieAtendidosShadow" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="0%" stopColor="#20c997" stopOpacity={0.12} />
                              <stop offset="100%" stopColor="#20c997" stopOpacity={0} />
                            </linearGradient>
                          </defs>
                          <Pie
                            data={graficos?.pizzaAtendidos?.dados ?? []}
                            dataKey="valor"
                            nameKey="label"
                            cx="50%"
                            cy="45%"
                            innerRadius={55}
                            outerRadius={95}
                            labelLine={false}
                            label={renderPieLabel}
                          >
                            {graficos?.pizzaAtendidos?.dados?.map((_, index) => (
                              <Cell
                                key={`atendidos-${index}`}
                                fill={PIE_COLORS[index % PIE_COLORS.length]}
                                stroke="none"
                              />
                            ))}
                          </Pie>
                          <Tooltip
                            formatter={(value: number, name: string) => [
                              value,
                              name,
                            ]}
                          />
                          <Legend 
                            verticalAlign="bottom" 
                            height={80}
                            wrapperStyle={{ paddingTop: "20px" }}
                          />
                        </PieChart>
                      </ResponsiveContainer>
                    </Styled.ChartBody>
                  </Styled.ChartCard>
                </Styled.ChartGrid>
              </Styled.Section>

              <Styled.Section>
                <Styled.SectionTitle>
                  Último atendimento por faixa
                </Styled.SectionTitle>
                <Styled.ChartCard $fullWidth>
                  <Styled.ChartCardHeader>
                    <div>
                      <Styled.ChartTitle>Visão em barras horizontais</Styled.ChartTitle>
                      <Styled.ChartDescription>
                        Mais contraste e rótulos legíveis para navegação por toque.
                      </Styled.ChartDescription>
                    </div>
                    <InfoModalButton
                      message={
                        graficos?.barUltimoAtendimento?.descricao ??
                        "Faixas do último atendimento."
                      }
                      isAbsolute={false}
                    />
                  </Styled.ChartCardHeader>

                  <Styled.ChartBody>
                    <ResponsiveContainer width="100%" height={260}>
                      <BarChart
                        layout="vertical"
                        data={graficos?.barUltimoAtendimento?.dados ?? []}
                        margin={{ top: 8, right: 24, left: 12, bottom: 8 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                        <XAxis type="number" />
                        <YAxis
                          type="category"
                          dataKey="label"
                          width={220}
                          tick={{ fontSize: 12 }}
                        />
                        <Tooltip />
                        <Bar
                          dataKey="valor"
                          fill="#0b5fff"
                          barSize={18}
                          radius={[12, 12, 12, 12]}
                        >
                          <LabelList
                            dataKey="valor"
                            position="right"
                            fill="#0b1d5d"
                            fontWeight={700}
                          />
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </Styled.ChartBody>
                </Styled.ChartCard>
              </Styled.Section>
            </>
          )}
        </>
      )}
    </Styled.Page>
  );
};

export { Indicators };
